import type { CabinetSummary } from "@planerius/contracts/cabinet"
import { type ReactNode, useCallback, useEffect, useState } from "react"

import { useHasPermission } from "../../access/use-access"
import { type ApiProblem, isApiProblem } from "../../api/problem"
import { fetchSummary } from "../../api/summary.api"
import { formatTimeOfDay } from "../../lib/format"
import { ProblemScreen, Skeleton } from "../../shell/states"
import { Button, EmptyState } from "../../ui"
import { StandHeader } from "../directories/stand"
import {
  dayLabel,
  hoursLabel,
  LOAD_FOOTNOTE,
  LOAD_FOOTNOTE_READONLY,
  LOAD_LOW_PERCENT,
  LOSSES_FOOTNOTE,
  LOSSES_FOOTNOTE_READONLY,
  LOSSES_NO_NAMES_TEXT,
  LOSSES_NO_NAMES_TITLE,
  lossKindLabel,
  lossRowCaption,
} from "./summary-model"
import styles from "./summary-screen.module.css"

/**
 * 5-В2 «Разбор: загрузка мастеров» и 5-В3 «Разбор: отмены и неявки».
 *
 * ОБА РАЗБОРА БЕРУТ ТОТ ЖЕ ЗАПРОС, ЧТО И СВОДКА. Строки разбора — это содержимое тех же
 * карточек, посчитанное тем же запросом: отдельный расчёт рано или поздно разошёлся бы
 * со сводкой, и человек увидел бы «64 %» на одном экране и «61 %» на другом, открытом
 * нажатием на первый.
 *
 * СТРОКА — ЭТО ДЕЙСТВИЕ, А НЕ ОТЧЁТ. Разбор открывают, чтобы пойти и поговорить:
 * с мастером про пустой день, с клиентом про вторую неявку. Поэтому каждая строка
 * ведёт туда, где разговор продолжается, — в день мастера и в саму запись.
 */

function useSummary(search: {
  readonly date?: string | undefined
  readonly branch?: string | undefined
}): {
  readonly summary: CabinetSummary | null
  readonly problem: ApiProblem | null
  readonly reload: () => void
} {
  const [summary, setSummary] = useState<CabinetSummary | null>(null)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const { date, branch } = search

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      try {
        const next = await fetchSummary(
          {
            ...(date === undefined ? {} : { date }),
            ...(branch === undefined ? {} : { branchId: branch }),
          },
          signal,
        )
        if (signal?.aborted === true) return
        setSummary(next)
        setProblem(null)
      } catch (error) {
        if (signal?.aborted === true) return
        if (!isApiProblem(error)) throw error
        setProblem(error)
      }
    },
    [date, branch],
  )

  useEffect(() => {
    const controller = new AbortController()
    void load(controller.signal)
    return () => controller.abort()
  }, [load])

  return { summary, problem, reload: () => void load() }
}

function Loading(): ReactNode {
  return (
    <div className={styles["rows"]} aria-busy="true" aria-live="polite">
      {[0, 1, 2].map((row) => (
        <Skeleton key={row} width="100%" height={64} radius={12} />
      ))}
    </div>
  )
}

export function SummaryLoadScreen(props: {
  readonly search: { readonly date?: string | undefined; readonly branch?: string | undefined }
  readonly onBack: () => void
  readonly onOpenEmployeeDay: (employeeId: string, branchId: string, date: string) => void
}): ReactNode {
  const { summary, problem, reload } = useSummary(props.search)
  // Строка ведёт в журнал, а журнал закрыт правом. У бухгалтера и наблюдателя его нет
  // намеренно — там имена клиентов, — и обещать им «нажатие открывает день мастера»
  // значит вести в отказ.
  const mayOpenJournal = useHasPermission("schedule.view")

  if (problem !== null && summary === null) {
    return <ProblemScreen problem={problem} onRetry={reload} />
  }

  const card = summary?.load
  const day = summary === null ? "" : ` ${dayLabel(summary.date, summary.scope.timezone)}`
  const network = (summary?.scope.branches.length ?? 0) > 1
  return (
    <div className={styles["screen"]}>
      <StandHeader title={card?.percent === undefined || card.percent === null
        ? `Загрузка мастеров${day}`
        : `Загрузка мастеров${day} · ${String(card.percent)} %`}>
        <Button size="sm" variant="secondary" onClick={props.onBack}>
          ‹ Сводка
        </Button>
      </StandHeader>

      {summary === null ? (
        <Loading />
      ) : card === undefined || card.status === "failed" ? (
        <EmptyState
          title="Загрузка не посчиталась"
          text="Остальные цифры сводки актуальны. Повторите — расчёт идёт по сменам и занятости за день."
          action={{ label: "Повторить", onClick: reload }}
        />
      ) : card.rows.length === 0 ? (
        <EmptyState
          title="Смен на этот день нет"
          text="Загрузку считать не от чего: без смены у мастера нет рабочего времени, к которому можно отнести занятость."
        />
      ) : (
        <>
          <div className={styles["rows"]}>
            {card.rows.map((row) => {
              const low = row.percent < LOAD_LOW_PERCENT
              const body = (
                <>
                  <span className={styles["rowHead"]}>
                    <span className={styles["rowName"]}>{row.displayName}</span>
                    <span className={low ? styles["rowValueLow"] : styles["rowValue"]}>
                      {String(row.percent)} %
                    </span>
                  </span>
                  <span className={styles["rowCaption"]}>
                    занято {hoursLabel(row.busyMinutes)} из {hoursLabel(row.workMinutes)}
                    {row.position === null ? "" : ` · ${row.position}`}
                    {/*
                      * НАЗВАНИЕ ТОЧКИ — В СЕТИ ОБЯЗАТЕЛЬНО. Мастер, работающий в двух
                      * филиалах в один день, даёт две строки, и «Анна 92 %» рядом с
                      * «Анна 18 %» без точки не даёт понять, где именно она простаивает.
                      */}
                    {network ? ` · ${row.branchName}` : ""}
                  </span>
                  {/*
                    * div, а не span: к строчному элементу не применяются ни height,
                    * ни заданная инлайном ширина — полоска не заполнялась никогда,
                    * и у 92 %, и у 18 % дорожка выглядела одинаково пустой.
                    */}
                  <div
                    className={styles["bar"]}
                    role="progressbar"
                    aria-valuenow={row.percent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`Загрузка: ${row.displayName}`}
                  >
                    <div
                      className={low ? styles["barFillLow"] : styles["barFill"]}
                      style={{ width: `${String(Math.min(100, row.percent))}%` }}
                    />
                  </div>
                </>
              )
              const key = `${row.employeeId}-${row.branchId}`
              return mayOpenJournal ? (
                <button
                  key={key}
                  type="button"
                  className={styles["row"]}
                  onClick={() => {
                    props.onOpenEmployeeDay(row.employeeId, row.branchId, summary.date)
                  }}
                >
                  {body}
                </button>
              ) : (
                <div key={key} className={styles["rowStatic"]}>
                  {body}
                </div>
              )
            })}
          </div>
          <p className={styles["footnote"]}>
            {mayOpenJournal ? LOAD_FOOTNOTE : LOAD_FOOTNOTE_READONLY}
          </p>
        </>
      )}
    </div>
  )
}

export function SummaryLossesScreen(props: {
  readonly search: { readonly date?: string | undefined; readonly branch?: string | undefined }
  readonly onBack: () => void
  readonly onOpenAppointment: (appointmentId: string, branchId: string | null, date: string) => void
}): ReactNode {
  const { summary, problem, reload } = useSummary(props.search)
  const mayOpenJournal = useHasPermission("schedule.view")

  if (problem !== null && summary === null) {
    return <ProblemScreen problem={problem} onRetry={reload} />
  }

  const card = summary?.losses
  const zone = summary?.scope.timezone ?? "Europe/Moscow"
  const day = summary === null ? "" : ` ${dayLabel(summary.date, zone)}`
  return (
    <div className={styles["screen"]}>
      <StandHeader title={card?.count === undefined || card.count === null
        ? `Отмены и неявки${day}`
        : `Отмены и неявки${day} · ${String(card.count)}`}>
        <Button size="sm" variant="secondary" onClick={props.onBack}>
          ‹ Сводка
        </Button>
      </StandHeader>

      {summary === null ? (
        <Loading />
      ) : card === undefined || card.status === "failed" ? (
        <EmptyState
          title="Разбор не посчитался"
          text="Остальные цифры сводки актуальны. Повторите — расчёт идёт по записям за день."
          action={{ label: "Повторить", onClick: reload }}
        />
      ) : card.count === 0 ? (
        // Пустой день здесь — хорошая новость, и сказать это надо словами: прочерк
        // читается как «не посчиталось».
        <EmptyState
          title="Ни одной отмены и ни одной неявки"
          text="Все записи дня на месте. Строки появятся, если кто-то отменит визит или не придёт."
        />
      ) : card.rows.length === 0 ? (
        // ЧИСЛО ЕСТЬ, А СТРОК НЕТ — значит, имена клиентов человеку не положены.
        // Сказать это прямо: молчащий экран под заголовком «Отмены и неявки · 3»
        // читается как поломка.
        <EmptyState title={LOSSES_NO_NAMES_TITLE} text={LOSSES_NO_NAMES_TEXT} />
      ) : (
        <>
          <div className={styles["rows"]}>
            {card.rows.map((row) => {
              const body = (
                <>
                  <span className={styles["rowHead"]}>
                    <span className={styles["rowName"]}>
                      {row.clientName} · {formatTimeOfDay(row.startAt, zone)}
                    </span>
                    <span className={styles["badge"]}>{lossKindLabel(row)}</span>
                  </span>
                  <span className={styles["rowCaption"]}>{lossRowCaption(row, zone)}</span>
                </>
              )
              return mayOpenJournal ? (
                <button
                  key={row.appointmentId}
                  type="button"
                  className={styles["row"]}
                  onClick={() => {
                    props.onOpenAppointment(row.appointmentId, null, summary.date)
                  }}
                >
                  {body}
                </button>
              ) : (
                <div key={row.appointmentId} className={styles["rowStatic"]}>
                  {body}
                </div>
              )
            })}
          </div>
          <p className={styles["footnote"]}>
            {mayOpenJournal ? LOSSES_FOOTNOTE : LOSSES_FOOTNOTE_READONLY}
          </p>
        </>
      )}
    </div>
  )
}
